import React, { useMemo, useState, useEffect } from "react";
import { observer } from "mobx-react";

import Board from "react-trello";
import { CSVLink } from "react-csv";

import * as commonS from "@styles/common";
import MainContentHeader from "@components/MainContentHeader";
import Loader from "@components/Loader";
import KidHint from "@components/Hints/KidHint";
import ClassHint from "@components/Hints/ClassHint";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import useCurrentProject from "@hooks/useCurrentProject";
import kidsApi from "@api/kids.api";

import { getGender, getGermanLanguageLevel } from "@helpers/getGermanNaming";
import stores from "@stores";

import * as S from "./styles";

interface Props {
  distributionMap: any;
  isLoading: boolean;
  isManualMode: boolean;
  enableManualMode: () => void;
  fetchDistribution: () => void;
  setIsLoading: (arg: boolean) => void;
  getBooleanLabel: (obj: any, key: string) => string;
  generateIcon: (obj: any, classItem: any) => any;
}

const KidsDistributionScreen = ({
  distributionMap,
  isLoading,
  fetchDistribution,
  enableManualMode,
  getBooleanLabel,
  setIsLoading,
  generateIcon,
  isManualMode,
}: Props) => {
  const {
    projectsStore: { filter, setFilterObj },
  } = stores;
  const [anchorEl, setAnchorEl] = useState(null);
  const [child, setChild] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [showDownloadLink, setShowDownloadLink] = useState(false);

  const { currentProject } = useCurrentProject();
  const open = Boolean(anchorEl);

  const id = open ? "simple-popover" : undefined;

  const handleChange = ({ target }: { target: HTMLInputElement }) => {
    const { name, value, checked } = target;

    setFilterObj({
      ...filter,
      [name]: Boolean(checked),
    });
  };

  useEffect(() => {
    fetchDistribution();
  }, [filter]);

  const handleClick = (event, child) => {
    setChild({ ...child });
    setAnchorEl(event.currentTarget);
  };

  const handleClickClass = (event, classObj) => {
    setClassInfo({ ...classObj });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setClassInfo(null);
    setChild(null);
    setAnchorEl(null);
  };

  const onManualModeChange = async (e: any) => {
    if (!e.target.checked) return;

    await enableManualMode();
  };

  const boardData = useMemo(() => {
    if (!distributionMap) {
      return { lanes: [] };
    }

    return {
      lanes: distributionMap
        .sort((a, b) =>
          a.institution.address.localeCompare(b.institution.address)
        )
        .map((classItem, index) => ({
          id: "class" + index,
          title: classItem.name,
          teacherName: classItem.teacherName,
          label: "Km",
          maxStudentsNumber: classItem?.maxStudentsNumber,
          minStudentsNumber: classItem?.minStudentsNumber,
          schoolBus: classItem?.kids.filter((e) => e.schoolBus),
          directAssignmentCount: classItem?.stats?.directAssignmentCount,
          femalesCount: classItem?.stats?.femalesCount,
          lowGermanLevelCount: classItem?.stats?.lowGermanLevelCount,
          malesCount: classItem?.stats?.malesCount,
          collapsed: classItem?.stats?.malesCount,
          onClick: classItem?.stats?.femalesCount,
          cards: classItem.kids
            .sort((a, b) => {
              if (a.lastName && b.lastName) {
                return a.lastName.localeCompare(b.lastName);
              } else {
                return a.fullName.localeCompare(b.fullName);
              }
            })
            .map((kid) => ({
              id: kid._id,
              title: `${kid.lastName} ${kid.firstName}`,
              label: kid.distanceToClass ? kid.distanceToClass.toFixed(2) : "",
              icon: generateIcon(kid, classItem),
              note: kid.note,
              gender: kid?.gender,
              address: kid?.address,
              germanLanguageLevel: kid?.germanLanguageLevel,
              motherLanguage: kid?.motherLanguage,
              previousExperience: kid?.previousExperience,
              schoolBus: kid?.schoolBus,
              teacherName: kid?.teacherName,
              birthday: kid?.birthday,
            }))
            .sort((a, b) => {
              if (a.title < b.title) {
                return -1;
              }
              if (a.title > b.title) {
                return 1;
              }
              return 0;
            }),
        })),
    };
  }, [distributionMap]);

  const handleMoveCard = async (cardId, _sourceLaneId, targetLaneId) => {
    const targetColumn = boardData?.lanes.filter(
      (e) => e.id === targetLaneId
    )[0];

    setIsLoading(true);

    try {
      await kidsApi.edit({
        teacherName: targetColumn?.teacherName,
        id: cardId,
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }

    await fetchDistribution();
  };

  const CustomCard = (props: any) => {
    const {
      title,
      label,
      total,
      icon,
      gender,
      address,
      note,
      germanLanguageLevel,
      motherLanguage,
      previousExperience,
      schoolBus,
      teacherName,
      birthday,
    } = props;
    const currentTarget = {
      fullName: title,
      gender: gender,
      address: address,
      germanLanguageLevel: germanLanguageLevel,
      motherLanguage,
      distanceToClass: label,
      previousExperience,
      schoolBus,
      teacherName,
      birthday,
      note,
    };
    return (
      <S.Card
        onClick={(e) => handleClick(e, currentTarget)}
        className={`card ${gender === "male" ? "blue" : "rose"}`}
      >
        <h3>
          {title}{" "}
          <span
            style={{
              fontSize: 12,
              fontWeight: 400,
              marginLeft: "auto",
              marginRight: "3px",
            }}
          >
            {icon?.lgIcon && <img src={icon.lgIcon} />}
            {icon?.daIcon && <img src={icon.daIcon} />}
          </span>{" "}
          <span style={{ minWidth: 40, textAlign: "right" }}>
            {label ? label : total}{" "}
          </span>
        </h3>
      </S.Card>
    );
  };

  const CustomHeader = (props: any) => {
    const {
      title,
      label,
      teacherName,
      maxStudentsNumber,
      minStudentsNumber,
      schoolBus,
      directAssignmentCount,
      femalesCount,
      lowGermanLevelCount,
      malesCount,
    } = props;
    const currentTarget = {
      name: title,
      teacherName,
      maxStudentsNumber,
      minStudentsNumber,
      schoolBus,
      directAssignmentCount,
      femalesCount,
      lowGermanLevelCount,
      malesCount,
    };
    return (
      <>
        <header onClick={(e) => handleClickClass(e, currentTarget)}>
          <span>{title}</span>
          <span>{label}</span>
          <strong
            style={{
              position: "absolute",
              bottom: -30,
              width: "100%",
              left: 0,
              padding: "0 15px",
            }}
          >
            Total: {femalesCount + malesCount}
          </strong>
        </header>
      </>
    );
  };

  const components = {
    Card: (props: any) => CustomCard({ ...props }),
    LaneHeader: (props: any) => CustomHeader({ ...props }),
  };

  const csvData = useMemo(() => {
    if (!distributionMap) return [];

    const rows: any[] = [];
    const headers = [
      "SuS_ID",
      "Name",
      "Vorname",
      "Adresse",
      "Geschlecht",
      "Lehrperson",
      "Geburtstag",
      "Schulbus",
      "Deutsch",
      "Umgangssprache",
      "Erfahrung Betreuung",
      "Eintritt ordentlich",
      "Notizen",
      "Wunsch",
      "Distanz"
    ];

    distributionMap.forEach((classItem) => {
      classItem.kids.forEach((kid) => {
        rows.push([
          kid.susId,
          kid.lastName,
          kid.firstName,
          kid.address,
          getGender(kid.gender),
          classItem.teacherName,
          kid.birthday,
          kid.schoolBus ? "ja" : "nein",
          getGermanLanguageLevel(kid.germanLanguageLevel),
          kid.motherLanguage,
          kid.previousExperience ? "ja" : "nein",
          "jA",
          kid.note,
          kid.specialNote,
          kid.distanceToClass ? kid.distanceToClass.toFixed(2) : ""
        ]);
      });
    });

    return [headers, ...rows];
  }, [distributionMap]);

  return (
    <commonS.MainBody>
      <MainContentHeader
        title={currentProject?.title || ""}
        buttonTitle="Daten-Export"
        isButtonDisabled={Boolean(!distributionMap)}
        onButtonClick={() => {
          setShowDownloadLink(true);
          setTimeout(() => setShowDownloadLink(false), 60000);
        }}
      />

      {showDownloadLink && (
        <CSVLink
          style={{ marginBottom: 20 }}
          onClick={() => setShowDownloadLink(false)}
          data={csvData}
        >
          Laden Sie hier die CSV-Datei herunter
        </CSVLink>
      )}

      <S.FilterWrap>
        <S.FilterContent className="content">
          <S.FilterItem>
            <FormControlLabel
              control={
                <Checkbox
                  name="gender"
                  onChange={handleChange}
                  disabled={!filter.germanLevel && !filter.distance}
                  checked={filter.gender}
                />
              }
              label="Geschlecht"
            />
          </S.FilterItem>
          <S.FilterItem>
            <FormControlLabel
              control={
                <Checkbox
                  name="distance"
                  onChange={handleChange}
                  disabled={!filter.gender && !filter.germanLevel}
                  checked={filter.distance}
                />
              }
              label="Distanz"
            />
          </S.FilterItem>
          <S.FilterItem>
            <FormControlLabel
              control={
                <Checkbox
                  name="germanLevel"
                  onChange={handleChange}
                  disabled={!filter.gender && !filter.distance}
                  checked={filter.germanLevel}
                />
              }
              label="Deutsch"
            />
          </S.FilterItem>
        </S.FilterContent>

        <FormControlLabel
          sx={{ marginLeft: "auto" }}
          control={
            <Switch
              checked={isManualMode}
              disabled={isManualMode}
              onChange={onManualModeChange}
            />
          }
          label="Manuelle Einteilung"
        />
      </S.FilterWrap>

      <S.Wrap>
        <Board
          data={boardData}
          handleDragEnd={handleMoveCard}
          components={components}
        />
      </S.Wrap>
      {open && child && (
        <KidHint
          kid={child}
          paperProps={{
            sx: {
              top: open
                ? `280px !important`
                : 0,
              left: open
                ? `${anchorEl?.getBoundingClientRect().left}px !important`
                : 0,
              transformOrigin: `280px -${anchorEl?.getBoundingClientRect().left}px !important`,
              transition: "opacity 0s ease !important",
            },
          }}
          handleClose={handleClose}
          anchorEl={anchorEl}
        />
      )}
      {open && classInfo && (
        <ClassHint
          classInfo={classInfo}
          paperProps={{
            sx: {
              top: open
                ? `280px !important`
                : 0,
              left: open
                ? `${anchorEl?.getBoundingClientRect().left}px !important`
                : 0,
              transformOrigin: `280px -${anchorEl?.getBoundingClientRect().left}px !important`,
              transition: "opacity 0s ease !important",
            },
          }}
          handleClose={handleClose}
          anchorEl={anchorEl}
        />
      )}
      {isLoading && <Loader filled={true} />}
    </commonS.MainBody>
  );
};

export default observer(KidsDistributionScreen);
